import { useState, useEffect, MouseEvent } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { defineMessages, useIntl } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useRoadblock from '@alltrails/layout/hooks/useRoadblock';
import useInstallPrompt from '@alltrails/install-prompt/hooks/useInstallPrompt';
import getStaticMapUrl from '@alltrails/core/utils/getStaticMapUrl';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import Image from '@alltrails/core/components/Image';
import Expand from '@alltrails/denali/icons/Expand';
import logTrailDetailsMapViewed from '@alltrails/amplitude/events/logTrailDetailsMapViewed';
import { BREAKPOINT_SM_SIZE, BREAKPOINT_MD_SIZE, BREAKPOINT_MD_MARGIN } from '@alltrails/denali/tokens';
import IconButton from '@alltrails/denali/components/IconButton';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import styles from './StaticMap.module.scss';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const InstallInterstitial = dynamic(() => import('@alltrails/install-prompt/components/InstallInterstitial'), { ssr: false });

const messages = defineMessages({
  ALT_EXPAND: { defaultMessage: 'Expand map' }
});

type StaticMapProps = {
  mapId: number;
  trailName: string;
  isMobile: boolean;
  isCard?: boolean;
  isScrollLimitActive?: boolean;
  trailId: number;
};

const StaticMap = ({ mapId, trailName, isMobile, isCard, isScrollLimitActive, trailId }: StaticMapProps) => {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { openTrailMap } = useIsTrailMapOpen();
  const [isInterstitialOpen, setIsInterstitialOpen] = useInstallPrompt('interstitial', false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const publicBaseUrl = usePublicBaseUrl();

  const cardMapSize = isMobile ? '320x320' : '1280x582';
  const staticMapUrl = getStaticMapUrl({ baseUrl: publicBaseUrl, id: mapId, scale: 2, size: isCard ? cardMapSize : '160x160', type: 'maps' });

  const onRoadblock = useRoadblock({
    afterRoadblock: openTrailMap,
    eventName: 'trail_explore',
    languageRegionCode,
    returnToUrl: `${pathname}?${searchParams}`,
    user,
    setIsInterstitialOpen
  });

  useEffect(() => {
    logTrailDetailsMapViewed({ trail_id: trailId });
  }, [trailId]);

  const logStaticMapClicked = () => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'view map',
      trail_page_location: isCard ? 'map card' : 'hero image inset'
    });
  };

  const sizes = isCard
    ? `(max-width: ${BREAKPOINT_MD_SIZE + BREAKPOINT_MD_MARGIN}px 1165px), (max-width: ${BREAKPOINT_SM_SIZE}px) 688px`
    : `(max-width: ${BREAKPOINT_SM_SIZE}px) 70px, 120px`;

  const staticImage = (
    <Image
      alt={intl.formatMessage({ defaultMessage: '{name} map' }, { name: trailName })}
      className={styles.staticMap}
      fill
      loading="eager"
      sizes={sizes}
      src={staticMapUrl}
    />
  );

  const onTrailMapClick = (e: MouseEvent) => {
    e.stopPropagation();
    logStaticMapClicked();
    if (user) {
      onRoadblock();
    } else {
      setIsSignupOpen(true);
    }
  };

  return (
    <div className={isCard ? styles.staticMapCard : styles.staticMapContainer}>
      <button data-testid="static-map-button" className={styles.button} onClick={onTrailMapClick} title={intl.formatMessage(messages.ALT_EXPAND)}>
        {staticImage}
      </button>
      {isCard && (
        <IconButton
          className={styles.expandButton}
          icon={{ Component: Expand }}
          onClick={onTrailMapClick}
          stopPropagation
          testId="static-map-icon-button"
          title={intl.formatMessage(messages.ALT_EXPAND)}
          variant="elevated"
        />
      )}
      {isSignupOpen && (
        <SignUpModal
          isOpen={isSignupOpen}
          closeModal={() => setIsSignupOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => setIsSignupOpen(false)}
          trigger="View Static Map"
          upsellTrigger="View Static Map"
        />
      )}
      {isInterstitialOpen && (
        <InstallInterstitial
          preserveInterstitial={false}
          isOpen={isInterstitialOpen}
          onRequestClose={() => setIsInterstitialOpen(false)}
          promptLocation="trail page"
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          isScrollLimitActive={isScrollLimitActive}
          variant="generic"
        />
      )}
    </div>
  );
};

export default StaticMap;
